import { Link } from "react-router-dom";
import { ExternalLink, Phone, ShieldCheck } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";

const CARD = "rounded-xl border border-[#E4E4E7] bg-white p-6 shadow-sm sm:p-8";
const H2 = "font-display text-xl font-semibold text-[#1A1A1A]";
const P = "mt-3 text-sm leading-relaxed text-[#52525B]";
const LINK = "font-medium text-[#E11D2A] transition-colors hover:text-[#F02837]";

export default function Spielerschutz() {
  return (
    <>
      <Seo
        title="Spielerschutz & Verantwortung | Sportwetten NRW"
        description="Spielerschutz bei Sportwetten NRW: Teilnahme ab 18 Jahren, Risiken problematischen Glücksspiels, kostenlose Hilfe der BZgA (0800 1372700) und unsere Maßnahmen vor Ort."
        path="/spielerschutz"
      />
      <section className="bg-[#FAFAFA]" data-testid="spielerschutz-page">
        <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6 lg:px-12 lg:py-24">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#E11D2A]">Verantwortung</p>
            <h1 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-[#1A1A1A] sm:text-4xl">
              Spielerschutz hat Vorrang
            </h1>
            <p className="mt-4 text-base leading-relaxed text-[#52525B]">
              Sportwetten sind ein Freizeitangebot für Erwachsene – kein Einkommensmodell und kein Weg
              aus finanziellen Schwierigkeiten. Wir nehmen unsere Verantwortung ernst und informieren
              transparent.
            </p>
          </Reveal>

          <div className="mt-10 space-y-6">
            <Reveal>
              <div className={CARD}>
                <div className="flex items-center gap-4">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-[#E11D2A] font-display text-base font-extrabold text-[#E11D2A]">18+</span>
                  <h2 className={H2}>Teilnahme erst ab 18 Jahren</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-[#52525B]">
                  Der Zutritt zu unseren Wettvermittlungsstellen und die Teilnahme an Sportwetten sind
                  Minderjährigen nicht gestattet. Unser Personal prüft im Zweifel das Alter anhand eines
                  amtlichen Ausweisdokuments.
                </p>
              </div>
            </Reveal>

            <Reveal>
              <div className={CARD}>
                <ShieldCheck className="h-6 w-6 text-[#E11D2A]" aria-hidden="true" />
                <h2 className={`mt-3 ${H2}`}>Risiken problematischen Glücksspiels</h2>
                <p className={P}>
                  Glücksspiel kann abhängig machen. Warnsignale sind unter anderem: häufigeres oder
                  längeres Spielen als geplant, Verschweigen des Spielverhaltens, Spielen zur Flucht vor
                  Problemen oder der Versuch, Verluste durch Weiterspielen auszugleichen. Nimm solche
                  Anzeichen ernst – bei dir selbst und bei anderen.
                </p>
              </div>
            </Reveal>

            <Reveal>
              <div className={CARD}>
                <Phone className="h-6 w-6 text-[#E11D2A]" aria-hidden="true" />
                <h2 className={`mt-3 ${H2}`}>Kostenlose Hilfe & Beratung</h2>
                <ul className="mt-4 space-y-3 text-sm leading-relaxed text-[#52525B]">
                  <li>
                    <strong className="text-[#1A1A1A]">BZgA-Telefonberatung „Check dein Spiel“:</strong>{" "}
                    <a href="tel:08001372700" className={LINK} data-testid="spielerschutz-hotline">
                      0800 1372700
                    </a>{" "}
                    – kostenlos, anonym und vertraulich.
                  </li>
                  <li>
                    <a href="https://www.check-dein-spiel.de" target="_blank" rel="noopener noreferrer" className={`inline-flex items-center gap-1 ${LINK}`} data-testid="spielerschutz-link-check-dein-spiel">
                      check-dein-spiel.de <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                    </a>{" "}
                    – Selbsttest und Beratungsangebote der Bundeszentrale für gesundheitliche Aufklärung.
                  </li>
                  <li>
                    <a href="https://www.spielen-mit-verantwortung.de" target="_blank" rel="noopener noreferrer" className={`inline-flex items-center gap-1 ${LINK}`} data-testid="spielerschutz-link-smv">
                      spielen-mit-verantwortung.de <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                    </a>{" "}
                    – Informationen zu Spielerschutz und Sperrsystemen.
                  </li>
                </ul>
              </div>
            </Reveal>

            <Reveal>
              <div className={CARD} data-testid="spielerschutz-regulatorik">
                <h2 className={H2}>Erlaubnis, OASIS & Aufsicht</h2>
                <p className={P}>
                  Unsere Wettvermittlungsstellen sind behördlich zugelassen. Zuständige Erlaubnis- und
                  Aufsichtsbehörden sind die Bezirksregierung Detmold (Standort Rietberg) und die
                  Bezirksregierung Münster (Standort Ahlen).
                </p>
                <p className={P}>
                  Wir sind an das länderübergreifende Spielersperrsystem <strong className="text-[#1A1A1A]">OASIS</strong> angeschlossen.
                  Wer sich sperren lässt, ist bei uns und bundesweit bei allen angeschlossenen
                  Anbietern von der Teilnahme ausgeschlossen. Informationen zur Selbst- und
                  Fremdsperre erhältst du bei unserem Personal oder unter{" "}
                  <a href="https://www.spielen-mit-verantwortung.de" target="_blank" rel="noopener noreferrer" className={LINK}>
                    spielen-mit-verantwortung.de
                  </a>
                  .
                </p>
              </div>
            </Reveal>

            <Reveal>
              <div className={CARD}>
                <h2 className={H2}>Fragen zum Spielerschutz?</h2>
                <p className={P}>
                  Sprich uns direkt in der Filiale an – unser Team ist geschult und nimmt sich Zeit.{" "}
                  <Link to="/standorte" className={LINK} data-testid="spielerschutz-link-standorte">
                    Standort in deiner Nähe finden
                  </Link>
                  .
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
